import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  ScrollView,
  Platform,
  TouchableOpacity,
  Animated,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { useTheme } from '../context/ThemeContext';
import { HEADING_FONT } from '../context/ThemeContext';

const TERMS_COLOR   = '#6366F1';
const PRIVACY_COLOR = '#10B981';
const WARN_COLOR    = '#EF4444';
const BRAND_COLOR   = '#FF8C42';

// ─── Content data ─────────────────────────────────────────────────────────────

const TERMS_SECTIONS = [
  {
    title: 'Academic Integrity',
    icon: 'school-outline',
    intro: 'Students must maintain the highest standards of academic integrity:',
    bullets: [
      'Do not share your account credentials with others',
      'Complete all quizzes and assessments independently',
      'Do not copy or plagiarize content from courses',
      'Report any suspicious activities to administrators',
    ],
  },
  {
    title: 'Course Participation',
    icon: 'library-outline',
    bullets: [
      'Actively engage with course materials and complete assignments',
      'Respect course deadlines when applicable',
      'Participate constructively in discussions and feedback',
      'Maintain regular progress in enrolled courses',
    ],
  },
  {
    title: 'Code of Conduct',
    icon: 'chatbubble-ellipses-outline',
    bullets: [
      'Use appropriate and professional language when using the AI Assistant',
      'Do not attempt to misuse the AI Assistant for inappropriate content',
      'Do not use offensive, abusive, or harmful language on the platform',
      'Report any technical issues or bugs to the administrators',
    ],
  },
  {
    title: 'Account Responsibilities',
    icon: 'person-circle-outline',
    bullets: [
      'Keep your account information accurate and up-to-date',
      'Protect your login credentials and do not share them',
      'Notify us immediately if you suspect unauthorized access',
      'One account per student — multiple accounts are prohibited',
    ],
  },
  {
    title: 'Certificate Authenticity',
    icon: 'ribbon-outline',
    bullets: [
      'Certificates are issued only upon genuine course completion',
      'Do not attempt to forge or manipulate certificates',
      'Certificates can be verified through our verification system',
      'Misrepresentation of certificates may result in account termination',
    ],
  },
];

const PRIVACY_SECTIONS = [
  {
    title: 'Information We Collect',
    icon: 'document-text-outline',
    intro: 'We collect information you provide directly to us:',
    bullets: [
      'Name, email address, and phone number',
      'Profile information and preferences',
      'Course enrollment and learning progress data',
      'Quiz scores, assessments, and certificates earned',
      'Communication and feedback you provide',
    ],
  },
  {
    title: 'How We Use Your Information',
    icon: 'analytics-outline',
    intro: 'We use the collected information to:',
    bullets: [
      'Provide and improve our learning services',
      'Track and display your learning progress',
      'Send notifications about courses and updates',
      'Generate and verify certificates',
      'Respond to your inquiries and support requests',
    ],
  },
  {
    title: 'Data Security',
    icon: 'shield-outline',
    intro: 'We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Your data is encrypted and stored securely on our servers.',
  },
  {
    title: 'Data Sharing',
    icon: 'share-social-outline',
    intro: 'We do not sell, trade, or rent your personal information to third parties. We may share anonymized, aggregated data for analytical and improvement purposes only.',
  },
  {
    title: 'Your Rights',
    icon: 'hand-left-outline',
    intro: 'You have the right to:',
    bullets: [
      'Access and view your personal data',
      'Request correction of inaccurate data',
      'Request deletion of your account',
      'Opt-out of marketing communications',
    ],
  },
  {
    title: 'Contact Us',
    icon: 'mail-outline',
    isContact: true,
  },
];

// ─── Section row ──────────────────────────────────────────────────────────────

const SectionRow = ({ index, section, accentColor, theme }) => (
  <View style={[styles.sectionRow, { borderLeftColor: accentColor }]}>
    <View style={styles.rowHeader}>
      <View style={[styles.numBadge, { backgroundColor: accentColor + '18' }]}>
        <Text style={[styles.numText, { color: accentColor }]}>
          {String(index).padStart(2, '0')}
        </Text>
      </View>
      <Text style={[styles.sectionTitle, { color: theme.colors.textPrimary }]} numberOfLines={1}>
        {section.title}
      </Text>
      <Icon name={section.icon} size={14} color={accentColor + 'BB'} />
    </View>

    <View style={styles.rowContent}>
      {section.isContact ? (
        <Text style={[styles.introText, { color: theme.colors.textSecondary }]}>
          If you have any questions about this policy, please contact us at{' '}
          <Text style={{ fontWeight: '700', color: theme.colors.textPrimary }}>
            skillspherefyp@gmail.com
          </Text>
        </Text>
      ) : (
        <>
          {section.intro != null && (
            <Text style={[styles.introText, { color: theme.colors.textSecondary }]}>
              {section.intro}
            </Text>
          )}
          {section.bullets && section.bullets.map((b, i) => (
            <View key={i} style={styles.bulletRow}>
              <View style={[styles.bulletDot, { backgroundColor: accentColor }]} />
              <Text style={[styles.bulletText, { color: theme.colors.textSecondary }]}>{b}</Text>
            </View>
          ))}
        </>
      )}
    </View>
  </View>
);

// ─── Main component ───────────────────────────────────────────────────────────

const PrivacyPolicyModal = ({ visible, onAccept, onCancel }) => {
  const { theme, isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('terms');
  const [tabBarWidth, setTabBarWidth] = useState(600);
  const tabAnim        = useRef(new Animated.Value(0)).current;
  const contentOpacity = useRef(new Animated.Value(1)).current;

  const switchTab = (tab) => {
    if (tab === activeTab) return;
    // Fade content out → swap → fade in
    Animated.timing(contentOpacity, { toValue: 0, duration: 80, useNativeDriver: true }).start(() => {
      setActiveTab(tab);
      Animated.timing(contentOpacity, { toValue: 1, duration: 150, useNativeDriver: true }).start();
    });
    // Slide indicator
    Animated.spring(tabAnim, {
      toValue: tab === 'terms' ? 0 : 1,
      useNativeDriver: false,
      friction: 10,
      tension: 140,
    }).start();
  };

  const indicatorLeft = tabAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, tabBarWidth / 2],
  });

  const accentColor = activeTab === 'terms' ? TERMS_COLOR : PRIVACY_COLOR;
  const sections    = activeTab === 'terms' ? TERMS_SECTIONS : PRIVACY_SECTIONS;

  const cardBg =
    isDark
      ? (Platform.OS === 'web' ? (theme.glass?.background ?? theme.colors.card) : theme.colors.card)
      : '#FFFFFF';

  const webGlassStyle =
    isDark && Platform.OS === 'web'
      ? {
          backdropFilter: `blur(${theme.glass?.backdropBlur ?? 16}px)`,
          WebkitBackdropFilter: `blur(${theme.glass?.backdropBlur ?? 16}px)`,
        }
      : {};

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => {}}>
      <View style={styles.overlay}>
        <View
          style={[
            styles.dialog,
            {
              backgroundColor: cardBg,
              borderColor: isDark ? (theme.glass?.border ?? theme.colors.border) : 'transparent',
              borderWidth: isDark ? 1 : 0,
              ...webGlassStyle,
            },
            isDark && theme.shadows?.xl,
          ]}
        >
          {/* Colored top rail — shifts with active tab */}
          <View style={[styles.topRail, { backgroundColor: accentColor }]} />

          {/* ── Header ───────────────────────────────────────────── */}
          <View style={styles.header}>
            <View style={[styles.brandPill, { backgroundColor: BRAND_COLOR + '15', borderColor: BRAND_COLOR + '30' }]}>
              <Icon name="shield-checkmark" size={15} color={BRAND_COLOR} />
              <Text style={[styles.brandPillText, { color: BRAND_COLOR, fontFamily: HEADING_FONT }]}>
                SkillSphere
              </Text>
            </View>
            <Text style={[styles.title, { color: theme.colors.textPrimary, fontFamily: HEADING_FONT }]}>
              Terms & Privacy Policy
            </Text>
            <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
              Please read and accept to continue using SkillSphere
            </Text>
          </View>

          {/* ── Tab bar ──────────────────────────────────────────── */}
          <View
            style={[styles.tabBar, { borderBottomColor: theme.colors.border }]}
            onLayout={(e) => setTabBarWidth(e.nativeEvent.layout.width)}
          >
            <TouchableOpacity style={styles.tab} onPress={() => switchTab('terms')} activeOpacity={0.7}>
              <Icon
                name="document-text"
                size={14}
                color={activeTab === 'terms' ? TERMS_COLOR : theme.colors.textTertiary}
              />
              <Text style={[
                styles.tabLabel,
                {
                  color: activeTab === 'terms' ? TERMS_COLOR : theme.colors.textTertiary,
                  fontWeight: activeTab === 'terms' ? '700' : '500',
                },
              ]}>
                Terms of Use
              </Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.tab} onPress={() => switchTab('privacy')} activeOpacity={0.7}>
              <Icon
                name="lock-closed"
                size={14}
                color={activeTab === 'privacy' ? PRIVACY_COLOR : theme.colors.textTertiary}
              />
              <Text style={[
                styles.tabLabel,
                {
                  color: activeTab === 'privacy' ? PRIVACY_COLOR : theme.colors.textTertiary,
                  fontWeight: activeTab === 'privacy' ? '700' : '500',
                },
              ]}>
                Privacy Policy
              </Text>
            </TouchableOpacity>

            {/* Animated sliding underline */}
            <Animated.View
              style={[
                styles.tabIndicator,
                { width: tabBarWidth / 2, left: indicatorLeft, backgroundColor: accentColor },
              ]}
            />
          </View>

          {/* ── Content (fades on tab switch) ────────────────────── */}
          <Animated.View style={[styles.contentWrapper, { opacity: contentOpacity }]}>
            <ScrollView
              style={styles.scroll}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.scrollContent}
            >
              {sections.map((section, idx) => (
                <SectionRow
                  key={`${activeTab}-${idx}`}
                  index={idx + 1}
                  section={section}
                  accentColor={accentColor}
                  theme={theme}
                />
              ))}

              {activeTab === 'terms' && (
                <View style={[styles.warningStrip, { backgroundColor: WARN_COLOR + '10', borderColor: WARN_COLOR + '30' }]}>
                  <Icon name="warning-outline" size={15} color={WARN_COLOR} />
                  <Text style={[styles.warningText, { color: WARN_COLOR }]}>
                    Violation of any of the above terms may result in immediate account deactivation without prior notice.
                  </Text>
                </View>
              )}
            </ScrollView>
          </Animated.View>

          {/* ── Footer ───────────────────────────────────────────── */}
          <View style={[styles.footer, { borderTopColor: theme.colors.border }]}>
            <Text style={[styles.disclaimer, { color: theme.colors.textTertiary }]}>
              By clicking "Accept", you agree to our Terms of Use and Privacy Policy. You will also be subscribed to the SkillSphere newsletter for blog updates, new courses, and learning tips. You can unsubscribe at any time.
            </Text>
            <View style={styles.buttonRow}>
              <TouchableOpacity
                style={[styles.declineBtn, { borderColor: theme.colors.border }]}
                onPress={onCancel}
                activeOpacity={0.7}
              >
                <Text style={[styles.declineText, { color: theme.colors.textSecondary }]}>Decline</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.acceptBtn, { backgroundColor: BRAND_COLOR }]}
                onPress={onAccept}
                activeOpacity={0.85}
              >
                <Icon name="checkmark-circle" size={17} color="#FFFFFF" />
                <Text style={styles.acceptText}>Accept & Continue</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    backgroundColor: 'rgba(0,0,0,0.65)',
  },
  dialog: {
    width: '100%',
    maxWidth: 640,
    maxHeight: '92%',
    borderRadius: 18,
    overflow: 'hidden',
  },
  topRail: {
    height: 3,
    width: '100%',
  },

  // Header
  header: {
    alignItems: 'center',
    paddingTop: 18,
    paddingHorizontal: 20,
    paddingBottom: 14,
  },
  brandPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 11,
    paddingVertical: 5,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: 10,
    gap: 6,
  },
  brandPillText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 5,
    letterSpacing: -0.2,
  },
  subtitle: {
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 19,
  },

  // Tabs
  tabBar: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    position: 'relative',
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 11,
    gap: 6,
  },
  tabLabel: {
    fontSize: 13,
    letterSpacing: 0.1,
  },
  tabIndicator: {
    position: 'absolute',
    bottom: 0,
    height: 2.5,
    borderRadius: 2,
  },

  // Content
  contentWrapper: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 8,
  },

  // Section rows
  sectionRow: {
    borderLeftWidth: 3,
    paddingLeft: 12,
    paddingTop: 10,
    paddingBottom: 10,
    marginBottom: 3,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  numBadge: {
    minWidth: 28,
    height: 20,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 5,
  },
  numText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    flex: 1,
  },
  rowContent: {
    paddingLeft: 36,
  },
  introText: {
    fontSize: 13,
    lineHeight: 19,
    marginBottom: 5,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 7,
    marginBottom: 3,
  },
  bulletDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginTop: 8,
  },
  bulletText: {
    fontSize: 13,
    lineHeight: 19,
    flex: 1,
  },

  // Warning
  warningStrip: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 11,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 10,
    marginBottom: 4,
  },
  warningText: {
    fontSize: 12,
    fontWeight: '600',
    flex: 1,
    lineHeight: 17,
  },

  // Footer
  footer: {
    paddingTop: 12,
    paddingBottom: 16,
    paddingHorizontal: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  disclaimer: {
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 16,
    marginBottom: 12,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
  },
  declineBtn: {
    flex: 1,
    height: 46,
    borderRadius: 23,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  declineText: {
    fontSize: 14,
    fontWeight: '600',
  },
  acceptBtn: {
    flex: 2.5,
    height: 46,
    borderRadius: 23,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 7,
  },
  acceptText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});

export default PrivacyPolicyModal;
